import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from '@stencil/core';

import { IUserInfo, MarketWebLoginManager } from '../../../globals/MarketWebLoginManager';
import { state as headerState } from './pn-marketweb-siteheader-store';
import { LoginDialog, SiteHeaderI18N } from './pn-marketweb-siteheader-types';
import { user } from 'pn-design-assets/pn-assets/icons.js';

@Component({
  tag: 'pn-marketweb-siteheader-login',
  styleUrl: 'pn-marketweb-siteheader-login.scss',
})
export class PnMarketwebSiteheaderLogin {
  @Element() hostElement: HTMLElement;
  /** Specifies which endpoint domain we should load from */
  @Prop() endpoint: string = '';
  /** Access token passed from backend */
  @Prop() token: string = '';
  @Prop() i18n: SiteHeaderI18N = {
    searchlabel: '',
    searchplaceholder: '',
    searchbuttontext: '',
    menuHomeButton: '',
    menuGoBackButton: '',
    mainMenuButton: '',
    menuStartButton: '',
    profileSelectionFlyoutHeading: '',
    unifiedLoginButton: '',
    unifiedMyPagesButton: '',
  } as SiteHeaderI18N;

  @Prop() siteUrl: string = '';
  @Prop() emitEvents: boolean = true;

  // Login dialog
  @Prop({ mutable: true }) loginDialog: LoginDialog = null;

  /** User Fullname */
  @Prop() fullname: string = '';
  @Prop() loggedin: boolean = false;

  /** If this is selected we will show the new profile selection dropdown instead of the login version */
  @Prop({ mutable: true }) showProfileSelection: boolean = false;

  /** if this is selected we will show the unifiedlogin button along with new look for dropdown */
  @Prop() showUnifiedLogin: boolean = false;

  /** Continiously check if a user is logged in **/
  @Prop({}) checkUserLoggedInStateInterval: number = 0;

  @Prop() environment?: string;

  @State() loginLinks: [];

  @State() toggleButtonText: string = this.loginDialog?.loginMenuLinkText ? this.loginDialog.loginMenuLinkText : '';

  @State() username: string = this.fullname;

  @Prop() useMarketwebLogin: boolean;

  // Events
  @Event() loginStateChange: EventEmitter;

  componentWillLoad() {
    const userInfo: IUserInfo = this.loggedin && this.fullname ? { given_name: this.fullname } : null;
    headerState.loginManager = new MarketWebLoginManager({
      endpoint: this.endpoint,
      eventTarget: this.hostElement as EventTarget,
      checkUserLoggedInStateInterval: this.checkUserLoggedInStateInterval,
      userInfo,
    });
    headerState.loginManager.setSiteUrl(this.siteUrl);
    this.hostElement['loginmanager'] = headerState.loginManager;
    if (this.token) {
      headerState.loginManager.registerToken(this.token, 'backend');
    } else if (this.emitEvents) {
      // The primary login instance
      headerState.loginManager.invalidateTokenOfType('backend');
    }
    this.hostElement.addEventListener(headerState.loginManager.events.loginstatechange, this.onLoginStateChange.bind(this));
    this.init();
    this.setUserName();
    this.setToggleButtonText();
  }

  @Watch('i18n')
  @Watch('loginDialog')
  async init() {
    headerState.loginManager.init(this.emitEvents);
    this.setToggleButtonText();
    this.adjustLoginLinks();
  }
  adjustLoginLinks() {
    if (!this.loginDialog?.loginMenuLinks) {
      return;
    }

    const adjustLink = (link, i) => {
      if (!link.isLogoutLink) {
        link.isLogoutLink = link.pageLink.indexOf('location.href') !== -1 && link.pageLink.indexOf('logout') !== -1;
      }
      if (link.isLogoutLink) {
        link.href = headerState.loginManager.getLogoutUrl(link.pageLink);
      }

      if (i === 0 && !link.href) {
        link.href = headerState.loginManager.getLoginUrl();
        link.linkType = 'primary';
      }
      if (link.primaryLinkApperance && !link.linkType) {
        link.linkType = 'primary';
      }
      return link;
    };
    if (this.loginDialog) {
      this.loginDialog.loginMenuLinks = this.loginDialog.loginMenuLinks.map(adjustLink);
      this.loginDialog.loggedInLinks = this.loginDialog.loggedInLinks.map(adjustLink);
    }
    this.loginDialog.legacyLoginLinks = [
      {
        href: headerState.loginManager.getLoginUrl(this.loginDialog.loginUrl),
        linkText: this.loginDialog.loginLinkText,
        openInNewWindow: false,
        isLogoutLink: false,
        pageLink: null,
        linkType: 'primary',
        primaryLinkApperance: true,
      },
      {
        href: headerState.loginManager.getLoginUrl(this.loginDialog.registerUrl),
        linkText: this.loginDialog.createLoginLinkText,
        openInNewWindow: false,
        isLogoutLink: false,
        pageLink: null,
        primaryLinkApperance: false,
      },
    ];
  }

  setUserName() {
    const userInfo = headerState.loginManager.getUserInfo();
    let name = '';
    if (!this.loggedin || !userInfo) {
      this.username = name;
    }
    if (userInfo && userInfo.given_name) {
      name = userInfo.given_name + (userInfo?.family_name ? ` ${userInfo?.family_name}` : '');
    }

    if (!name && userInfo && userInfo.email) {
      name = userInfo.email;
    }
    if (!name && this.fullname) {
      name = this.fullname;
    }

    this.username = name;

    this.adjustLoginLinks();
  }

  setToggleButtonText() {
    if (this.loggedin) {
      this.toggleButtonText = this.username;
      if (!this.toggleButtonText) {
        this.toggleButtonText = this.loginDialog.loggedInButtonText;
      }
    } else {
      this.toggleButtonText = this.loginDialog.loginMenuLinkText;
    }
  }

  onLoginStateChange(e: CustomEvent) {
    if (this.loggedin !== e.detail && this.emitEvents) {
      this.loggedin = e.detail;
      this.loginStateChange.emit({ loggedIn: this.loggedin, token: headerState.loginManager.getToken() });
      this.setUserName();
      this.setToggleButtonText();
    }
  }

  render() {
    if (!this.loginDialog) {
      return;
    }

    let hostElementAttribute = {
      username: null,
      loggedin: 'false',
    };
    if (this.username) {
      hostElementAttribute.username = this.username;
      hostElementAttribute.loggedin = this.loggedin + '';
    }
    return (
      <Host {...hostElementAttribute}>
        <pn-button-dropdown label={this.toggleButtonText} icon={user} appearance="light" variant="borderless" class="siteheader-logindialog">
          {this.showUnifiedLogin && !this.loggedin && (
            <pn-marketweb-siteheader-unified-login
              loggedIn={this.loggedin}
              logInLabel={this.i18n.unifiedLoginButton}
              useMarketwebLogin={this.useMarketwebLogin}
              environment={this.environment}
            />
          )}
          {this.loggedin && this.showProfileSelection ? (
            <pn-marketweb-siteheader-login-profileselection
              loginDialog={this.loginDialog}
              loggedin={this.loggedin}
              i18n={this.i18n}
              idNamespace={this.emitEvents ? '1' : '2'}
              endpoint={this.endpoint}
            ></pn-marketweb-siteheader-login-profileselection>
          ) : (
            <pn-marketweb-siteheader-login-links
              loginDialog={this.loginDialog}
              loggedin={this.loggedin}
              idNamespace={this.emitEvents ? '1' : '2'}
              username={this.username}
              showUnifiedLogin={this.showUnifiedLogin}
            ></pn-marketweb-siteheader-login-links>
          )}
          {!this.loggedin && this.loginDialog?.loginInfo && ( <span innerHTML={`${this.loginDialog.loginInfo}`} />)}
        </pn-button-dropdown>
      </Host>
    );
  }
}
